import { Link } from "react-router-dom";
import BookingLink from "@/components/BookingLink";
import { Button } from "@/components/ui/button";
import PageLayout from "@/components/PageLayout";
import {
  ShieldCheck,
  ClipboardCheck,
  Sparkles,
  ArrowRight,
  GraduationCap,
  Award,
  Building2,
  Search,
  Gauge,
  Scale,
  BadgeCheck,
} from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import heroWorkplace from "@/assets/hero-ai-workplace.webp";


const audiences = [
  {
    icon: GraduationCap,
    name: "Training Providers",
    headline: "Strengthen quality. Improve performance. Govern AI responsibly.",
    description:
      "Helping apprenticeship and training providers strengthen quality systems, prepare for inspection, understand AI risk and improve how their organisations operate.",
    tags: ["Quality Assurance", "Ofsted Readiness", "AI Governance", "Shadow AI", "Data & Systems", "AI Readiness"],
    cta: "Explore Training Provider Support",
    link: "/training-providers",
  },
  {
    icon: Award,
    name: "Awarding & Assessment Organisations",
    headline: "Protect assessment integrity as AI changes qualification and assessment.",
    description:
      "Independent assurance and governance support for organisations responsible for qualifications, assessment and certification.",
    tags: ["Assessment Integrity", "AI Assessment Assurance", "AI Governance", "AI Malpractice Risk", "AI in Marking & QA", "Responsible Innovation"],
    cta: "Explore Awarding & Assessment Support",
    link: "/awarding-organisations",
  },
  {
    icon: Building2,
    name: "Businesses & SMEs",
    headline: "Turn AI opportunity into responsible implementation.",
    description:
      "Helping organisations identify where AI can create value, manage emerging risks and introduce automation responsibly.",
    tags: ["AI Readiness", "AI Strategy", "AI Governance", "Shadow AI", "Automation", "AI Implementation"],
    cta: "Explore Business Support",
    link: "/ai-for-business",
  },
];

const capabilities = [
  {
    icon: ClipboardCheck,
    title: "Quality & Assurance",
    summary: "Build stronger systems around quality, performance and accountability.",
    areas: ["Quality frameworks", "Independent reviews", "Assessment assurance", "Quality improvement", "IQA", "Systems and process review"],
    cta: "Explore Quality & Assurance",
    link: "/services",
  },
  {
    icon: ShieldCheck,
    title: "AI Governance & Assurance",
    summary: "Understand where AI is being used, what risks it creates and whether your controls are working.",
    areas: ["AI governance frameworks", "AI risk", "Shadow AI", "AI registers", "Human oversight", "Supplier assurance", "ISO/IEC 42001 alignment", "Independent AI assurance"],
    cta: "Explore AI Governance",
    link: "/ai-for-business",
  },
  {
    icon: Sparkles,
    title: "AI Transformation",
    summary: "Identify where AI and automation can create meaningful value and implement change responsibly.",
    areas: ["AI readiness", "AI strategy", "Automation", "Workflow redesign", "Process improvement", "AI implementation", "Change readiness", "AI leadership"],
    cta: "Explore AI Transformation",
    link: "/ai-for-business",
  },
];

const governanceStages = [
  { title: "Discover", description: "Identify formal, informal and embedded AI use." },
  { title: "Assess", description: "Understand risk, impact and accountability." },
  { title: "Govern", description: "Introduce proportionate controls and oversight." },
  { title: "Assure", description: "Establish whether governance arrangements work in practice." },
];

const qualityAreas = [
  "Independent Review",
  "Quality Improvement",
  "Assessment Assurance",
  "Systems & Process Review",
  "Leadership Assurance",
];

const transformationFlow = ["Problem", "Opportunity", "AI / Automation / Redesign", "Risk", "Implement", "Measure"];

const featuredServices = [
  { icon: Search, title: "Shadow AI Audit", description: "Discover how AI is actually being used across your organisation.", link: "/ai-for-business" },
  { icon: ShieldCheck, title: "AI Governance Review", description: "Understand whether your AI governance arrangements are proportionate and effective.", link: "/ai-for-business" },
  { icon: Award, title: "AI Assessment Risk & Readiness Review", description: "Understand how AI could affect qualification and assessment integrity.", link: "/awarding-organisations" },
  { icon: ClipboardCheck, title: "Independent Quality Review", description: "Gain an objective view of quality, systems, risk and performance.", link: "/services" },
  { icon: Gauge, title: "AI Readiness & Opportunity Review", description: "Identify where AI could create meaningful value and what needs to be in place first.", link: "/ai-for-business" },
  { icon: Scale, title: "AI Leadership & Governance Workshop", description: "Give leaders the knowledge to understand AI opportunity, risk and accountability.", link: "/ai-leadership-accelerator" },
];

const principles = [
  { title: "Assurance-led", description: "We look beyond whether a policy or process exists and examine whether it works in practice." },
  { title: "Independent", description: "We provide an objective view of quality, risk, controls and readiness." },
  { title: "Practical", description: "Recommendations need to work within real organisations, not simply look good in reports." },
  { title: "Responsible Innovation", description: "Good governance should enable responsible progress rather than unnecessarily restrict it." },
];

const evidenceChain = ["Risk", "Control", "Test", "Evidence", "Monitor"];

const expertiseStrip = [
  "Quality Assurance",
  "Assessment",
  "AI Governance",
  "ISO/IEC 42001",
  "Apprenticeships",
  "Responsible AI",
];

const latestPosts = [...blogPosts]
  .sort((a, b) => b.publishedDate.localeCompare(a.publishedDate))
  .slice(0, 3);

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://qa1st.com/#organization",
      name: "QA 1st",
      url: "https://qa1st.com",
      description:
        "QA 1st helps training providers, Awarding & Assessment Organisations and businesses strengthen quality, govern AI responsibly and introduce new technology with confidence.",
      areaServed: "United Kingdom",
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": "https://qa1st.com/#website",
      url: "https://qa1st.com",
      name: "QA 1st",
      publisher: { "@id": "https://qa1st.com/#organization" },
      inLanguage: "en-GB",
    },
  ],
};

const FlowArrow = () => (
  <div className="flex items-center justify-center text-accent" aria-hidden="true">
    <ArrowRight className="h-5 w-5 rotate-90 md:rotate-0" />
  </div>
);

const Index = () => {
  return (
    <PageLayout
      title="QA 1st | Quality Assurance, AI Governance & Responsible AI"
      description="QA 1st helps training providers, awarding organisations and businesses strengthen quality and govern AI responsibly."
      jsonLd={organizationJsonLd}
    >
      {/* Hero */}
      <section className="bg-primary section-padding relative overflow-hidden">
        <img
          src={heroWorkplace}
          alt="Senior leadership team reviewing AI governance and quality assurance data in a modern UK office"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary/70" />
        <div className="container-narrow relative z-10 max-w-4xl mx-auto text-center">

          <p className="text-accent text-xs md:text-sm font-semibold tracking-[0.2em] uppercase mb-6">
            Quality | AI Governance | Assurance
          </p>
          <h1 className="text-primary-foreground mb-6">Quality and assurance for the AI era.</h1>
          <p className="text-primary-foreground/80 text-lg md:text-xl mb-5 max-w-3xl mx-auto">
            Helping training providers, Awarding &amp; Assessment Organisations and businesses strengthen quality,
            govern AI responsibly and introduce new technology with confidence.
          </p>
          <p className="text-primary-foreground/60 text-base mb-8 max-w-3xl mx-auto">
            QA 1st combines quality assurance, AI governance and transformation expertise to help organisations
            understand risk, improve systems and adopt AI responsibly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" asChild>
              <BookingLink>Start a Conversation</BookingLink>
            </Button>
            <Button variant="hero-outline" asChild>
              <Link to="/services">Explore How We Can Help</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10">
            {audiences.map((a) => (
              <Link
                key={a.name}
                to={a.link}
                className="group border border-primary-foreground/20 rounded-lg px-4 py-3 text-left text-primary-foreground/90 hover:border-accent hover:text-accent transition-colors flex items-center justify-between gap-2"
              >
                <span className="text-sm font-medium">{a.name}</span>
                <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>

          <p className="text-primary-foreground/50 text-xs md:text-sm mt-8 tracking-wide">
            Quality Assurance &nbsp;|&nbsp; AI Governance &nbsp;|&nbsp; AI Assurance &nbsp;|&nbsp; ISO/IEC 42001 &nbsp;|&nbsp; Responsible AI
          </p>
        </div>
      </section>

      {/* Who we help */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="mb-4">Specialist support where quality and trust matter.</h2>
            <p className="text-muted-foreground">
              Our approach adapts to the different quality, governance and transformation challenges facing
              education, assessment and business organisations.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {audiences.map((a) => (
              <div key={a.name} className="bg-card border border-border rounded-lg p-7 flex flex-col card-hover">
                <a.icon className="h-8 w-8 text-accent mb-5" />
                <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-3">{a.name}</p>
                <h3 className="text-lg font-semibold mb-3">{a.headline}</h3>
                <p className="text-muted-foreground text-sm mb-5">{a.description}</p>
                <ul className="flex flex-wrap gap-2 mb-6">
                  {a.tags.map((t) => (
                    <li key={t} className="text-xs bg-secondary text-secondary-foreground rounded-full px-3 py-1">
                      {t}
                    </li>
                  ))}
                </ul>
                <Link
                  to={a.link}
                  className="mt-auto text-accent text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {a.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three core capabilities */}
      <section className="section-padding bg-card">
        <div className="container-narrow">
          <h2 className="text-center mb-12">Three capabilities. One assurance-led approach.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {capabilities.map((c) => (
              <div key={c.title} className="bg-background border border-border rounded-lg p-7 flex flex-col">
                <c.icon className="h-8 w-8 text-accent mb-5" />
                <h3 className="text-lg font-semibold mb-2">{c.title}</h3>
                <p className="text-muted-foreground text-sm mb-5">{c.summary}</p>
                <ul className="space-y-2 mb-6 text-sm text-foreground">
                  {c.areas.map((area) => (
                    <li key={area} className="flex items-start gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent mt-2 shrink-0" />
                      {area}
                    </li>
                  ))}
                </ul>
                <Link
                  to={c.link}
                  className="mt-auto text-accent text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
                >
                  {c.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Governance feature */}
      <section className="section-padding bg-primary">
        <div className="container-narrow">
          <div className="max-w-3xl">
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              AI Governance &amp; Assurance
            </p>
            <h2 className="text-primary-foreground mb-6">
              Do you know how AI is actually being used across your organisation?
            </h2>
            <p className="text-primary-foreground/70 mb-4">
              AI adoption rarely begins with a formal strategy. Staff experiment with new tools. AI becomes embedded
              within existing software. Suppliers introduce new functionality. Teams create their own ways of working.
            </p>
            <p className="text-primary-foreground/70 mb-8">
              Before long, organisations can have significantly more AI activity than leadership realises.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-primary-foreground border-l-2 border-accent pl-5 mb-12">
              You cannot govern what you cannot see.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-4 items-stretch mb-12">
            {governanceStages.map((stage, i) => (
              <div key={stage.title} className="contents">
                <div className="border border-primary-foreground/20 rounded-lg p-5">
                  <p className="text-accent text-xs font-semibold tracking-widest uppercase mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="text-primary-foreground text-base font-semibold mb-2">{stage.title}</h3>
                  <p className="text-primary-foreground/60 text-sm">{stage.description}</p>
                </div>
                {i < governanceStages.length - 1 && <FlowArrow />}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="hero" asChild>
              <Link to="/ai-for-business">Explore AI Governance</Link>
            </Button>
            <Button variant="hero-outline" asChild>
              <Link to="/ai-for-business">Discover the Shadow AI Audit</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quality & Assurance */}
      <section className="section-padding">
        <div className="container-narrow grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">Quality &amp; Assurance</p>
            <h2 className="mb-6">
              Quality systems should help organisations improve, not simply prove compliance.
            </h2>
            <p className="text-muted-foreground mb-4">
              QA 1st combines independent assurance with practical improvement.
            </p>
            <p className="text-muted-foreground mb-8">
              We help organisations understand how systems, processes, people and evidence work together, identify
              where quality is being weakened and strengthen the controls that support sustainable performance.
            </p>
            <Link
              to="/services"
              className="text-accent text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              Explore Quality &amp; Assurance <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="space-y-3">
            {qualityAreas.map((area) => (
              <li
                key={area}
                className="bg-card border border-border rounded-lg px-5 py-4 flex items-center gap-3 text-foreground font-medium"
              >
                <BadgeCheck className="h-5 w-5 text-accent shrink-0" />
                {area}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* AI Transformation */}
      <section className="section-padding bg-card">
        <div className="container-narrow">
          <div className="max-w-3xl mb-12">
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">AI Transformation</p>
            <h2 className="mb-6">Move from AI experimentation to meaningful value.</h2>
            <p className="text-muted-foreground mb-4">
              The strongest AI opportunities are not always the most complicated. They often sit within repetitive
              work, duplicated processes, fragmented systems, manual reporting and activities that consume valuable
              staff capacity.
            </p>
            <p className="text-muted-foreground">
              QA 1st helps organisations identify where AI, automation or process redesign can create genuine value
              while considering governance and risk from the beginning.
            </p>
          </div>

          <ol className="flex flex-col md:flex-row md:items-center gap-3 mb-10">
            {transformationFlow.map((step, i) => (
              <li key={step} className="flex flex-col md:flex-row md:items-center gap-3 md:flex-1">
                <span className="bg-background border border-border rounded-lg px-4 py-3 text-sm font-medium text-foreground text-center w-full">
                  {step}
                </span>
                {i < transformationFlow.length - 1 && <FlowArrow />}
              </li>
            ))}
          </ol>

          <Link
            to="/ai-for-business"
            className="text-accent text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
          >
            Explore AI Transformation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Featured services */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="mb-4">Start with the challenge you need to solve.</h2>
            <p className="text-muted-foreground">
              You do not need to know which service you need before speaking to us. Start with the challenge.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredServices.map((s) => (
              <Link key={s.title} to={s.link} className="bg-card border border-border rounded-lg p-6 card-hover group flex flex-col">
                <s.icon className="h-7 w-7 text-accent mb-4" />
                <h3 className="text-base font-semibold mb-2 group-hover:text-accent transition-colors">{s.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">{s.description}</p>
                <span className="mt-auto text-accent text-sm font-medium inline-flex items-center gap-1">
                  Explore <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why QA 1st */}
      <section className="section-padding bg-card">
        <div className="container-narrow">
          <div className="max-w-3xl mb-12">
            <h2 className="mb-4">Why QA 1st?</h2>
            <p className="text-muted-foreground">
              Technology changes quickly. The principles of good quality, governance and accountability remain
              essential.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {principles.map((p) => (
              <div key={p.title} className="bg-background border border-border rounded-lg p-6">
                <h3 className="text-base font-semibold mb-2">{p.title}</h3>
                <p className="text-muted-foreground text-sm">{p.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-background border border-border rounded-lg p-8 md:p-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Move from policy to evidence.</h3>
            <p className="text-muted-foreground max-w-3xl mb-8">
              Our approach connects risk, controls, implementation and evidence so leaders can demonstrate not only
              what should happen, but what is actually happening.
            </p>
            <ol className="flex flex-col md:flex-row md:items-center gap-3">
              {evidenceChain.map((step, i) => (
                <li key={step} className="flex flex-col md:flex-row md:items-center gap-3 md:flex-1">
                  <span className="border border-border rounded-lg px-4 py-3 text-sm font-medium text-foreground text-center w-full">
                    {step}
                  </span>
                  {i < evidenceChain.length - 1 && <FlowArrow />}
                </li>
              ))}
            </ol>
          </div>

          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-3 mt-12 text-sm text-muted-foreground">
            {expertiseStrip.map((item) => (
              <li key={item} className="font-medium">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Insights */}
      <section className="section-padding">
        <div className="container-narrow">
          <div className="max-w-2xl mb-10">
            <h2 className="mb-4">Latest Insights</h2>
            <p className="text-muted-foreground">
              Practical thinking on quality, AI governance, assessment and responsible transformation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {latestPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="bg-card border border-border rounded-lg p-6 card-hover group flex flex-col"
              >
                <span className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">
                  {post.category}
                </span>
                <h3 className="text-base font-semibold mb-2 group-hover:text-accent transition-colors">
                  {post.title}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">{post.summary}</p>
                <span className="mt-auto text-xs text-muted-foreground">{post.readTime}</span>
              </Link>
            ))}
          </div>
          <Link
            to="/blog"
            className="text-accent text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all"
          >
            View All Insights <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-primary">
        <div className="container-narrow text-center max-w-3xl mx-auto">
          <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">Start a Conversation</p>
          <h2 className="text-primary-foreground mb-6">Where does your organisation need greater confidence?</h2>
          <p className="text-primary-foreground/70 mb-8">
            Whether you are strengthening quality, responding to emerging AI risks or exploring where AI can create
            value, QA 1st can help you understand where you are now and identify the right route forward.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="hero" asChild>
              <BookingLink>Start a Conversation</BookingLink>
            </Button>
            <Button variant="hero-outline" asChild>
              <Link to="/services">Explore Our Services</Link>
            </Button>
          </div>
          <p className="text-primary-foreground/50 text-sm mt-8 tracking-wide">
            Quality. Governance. Assurance. Transformation.
          </p>
        </div>
      </section>
    </PageLayout>
  );
};

export default Index;
